import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import Styles from './PeakHourCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import ChartComponent from 'Components/ChartComponent/ChartComponent'
import IFText from 'Components/IFText/IFText'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import IFSkeleton from 'Components/IFSkeleton/IFSkeleton'
import { useApexResizeObserver } from 'Utils/Resizeobserver'

const PeakHourCard = ({
  fetchPeakHour,
  peakHour,
  filter,
  fetchPeakHourRequestState,
}) => {
  const chartWrapperRef = useApexResizeObserver()
  const [selectedCell, setSelectedCell] = useState(null)
  const { t } = useTranslation()

  useEffect(() => {
    fetchPeakHour(filter)
    setSelectedCell(null)
  }, [filter])

  const dayMapping = {
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
    Sun: 'Sunday',
  }

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const hours = [
    '12 am',
    '2 am',
    '4 am',
    '6 am',
    '8 am',
    '10 am',
    '12 pm',
    '2 pm',
    '4 pm',
    '6 pm',
    '8 pm',
    '10 pm',
  ]

  const getHourRange = (index) => {
    const start = hours[index]
    const end = hours[index + 1] || '12 am'
    return `${start} – ${end}`
  }

  const series = hours
    .map((hour, hourIndex) => ({
      name: hour,
      data: days.map((day, dayIndex) => {
        const value = peakHour?.data?.heatmap?.[dayIndex]?.[hourIndex]
        return {
          x: day,
          y: value !== undefined && value !== null ? value : 0,
        }
      }),
    }))
    .reverse()

  const allValues =
    peakHour?.data?.heatmap?.flat().filter((v) => typeof v === 'number') || []

  const minValue = allValues.length ? Math.min(...allValues) : 0
  const maxValue = allValues.length ? Math.max(...allValues) : 0
  const isAllZero = maxValue === 0 && minValue === 0
  const allZero = allValues.every((v) => v === 0)
  const shadeIntensity = allZero ? 0.5 : 0

  let maxCell = null
  if (peakHour?.data?.heatmap?.length) {
    let max = -Infinity
    let maxDayIndex = 0
    let maxHourIndex = 0

    peakHour.data.heatmap.forEach((dayArr, dayIndex) => {
      dayArr.forEach((val, hourIndex) => {
        if (typeof val === 'number' && val > max) {
          max = val
          maxDayIndex = dayIndex
          maxHourIndex = hourIndex
        }
      })
    })

    if (max !== -Infinity) {
      maxCell = {
        day: days[maxDayIndex],
        hour: getHourRange(maxHourIndex),
        value: max,
      }
    }
  }
  const cellToDisplay = selectedCell || maxCell

  const defaultRanges = [
    {
      from: 81,
      to: Infinity,
      name: '+80',
      color: Colors.Analytics.PeakHour.Shade1,
    },
    { from: 61, to: 80, name: '≥ 60', color: Colors.Analytics.PeakHour.Shade2 },
    { from: 41, to: 60, name: '≥ 40', color: Colors.Analytics.PeakHour.Shade3 },
    { from: 21, to: 40, name: '≥ 20', color: Colors.Analytics.PeakHour.Shade4 },
    { from: 11, to: 20, name: '< 20', color: Colors.Analytics.PeakHour.Shade5 },
    { from: 1, to: 10, name: '< 10', color: Colors.Analytics.PeakHour.Shade6 },
    { from: 0, to: 0, name: '0', color: Colors.Analytics.PeakHour.Shade7 },
  ].reverse()

  const ranges = (() => {
    if (isAllZero) {
      return defaultRanges
    }

    if (maxValue <= 100) {
      return defaultRanges
    }

    const numRanges = 6
    const nonZeroMin = Math.min(...allValues.filter((v) => v > 0)) || 1
    const step = (maxValue - nonZeroMin) / numRanges

    const dynamicRanges = []

    dynamicRanges.push({
      from: 0,
      to: 0,
      name: '0',
      color: Colors.Analytics.PeakHour.Shade7,
    })

    for (let i = 0; i < numRanges; i++) {
      const rangeStart = i === 0 ? 1 : Math.ceil(nonZeroMin + i * step)
      const rangeEnd =
        i === numRanges - 1
          ? maxValue + 1000
          : Math.floor(nonZeroMin + (i + 1) * step)

      dynamicRanges.push({
        from: rangeStart,
        to: rangeEnd,
        name: `≥ ${rangeStart}`,
        color: [
          Colors.Analytics.PeakHour.Shade6,
          Colors.Analytics.PeakHour.Shade5,
          Colors.Analytics.PeakHour.Shade4,
          Colors.Analytics.PeakHour.Shade3,
          Colors.Analytics.PeakHour.Shade2,
          Colors.Analytics.PeakHour.Shade1,
        ][i],
      })
    }

    return dynamicRanges
  })()

  const options = {
    chart: {
      type: 'heatmap',
      toolbar: { show: false },
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: {
          enabled: true,
          delay: 50,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 500,
        },
      },
      events: {
        dataPointSelection: (
          event,
          chartContext,
          { seriesIndex, dataPointIndex },
        ) => {
          const hour = getHourRange(series.length - 1 - seriesIndex)
          const day = days[dataPointIndex]
          const value = series[seriesIndex]?.data[dataPointIndex]?.y
          setSelectedCell({ day, hour, value })
        },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity,
        enableShades: false,
        colorScale: {
          min: 0,
          max: Math.max(maxValue, 1),
          inverse: true,
          ranges,
        },
      },
    },
    fill: { opacity: 1 },
    dataLabels: { enabled: false },
    xaxis: { categories: days },
    legend: { position: 'bottom' },
  }

  return (
    <div style={{ backgroundColor: Colors.white }} className={Styles.Card}>
      {fetchPeakHourRequestState === RequestState.LOADING ? (
        <>
          <div
            style={{
              backgroundColor: Colors.Analytics.PeakHour.PeakHourBackground,
              border: `1px solid ${Colors.Analytics.PeakHour.PeakHourBorder}`,
            }}
            className={Styles.Top}
          >
            <div className={Styles.DayConatiner}>
              <IFSkeleton
                variant="text"
                width={120}
                height={16}
                className={Styles.TopDay}
              />
              <IFSkeleton
                variant="text"
                width={120}
                height={16}
                className={Styles.Session}
              />
            </div>
            <IFSkeleton
              variant="text"
              width={60}
              height={16}
              className={Styles.Session}
            />
          </div>
          <IFSkeleton
            className={Styles.ChartSkeleton}
            variant="rectangular"
            height={'100%'}
            width={'100%'}
          />
        </>
      ) : (
        <div className={Styles.MainContainer}>
          <div
            style={{
              backgroundColor: Colors.Analytics.PeakHour.PeakHourBackground,
              border: `1px solid ${Colors.Analytics.PeakHour.PeakHourBorder}`,
            }}
            className={Styles.Top}
          >
            {cellToDisplay && (
              <>
                <div className={Styles.DayConatiner}>
                  <IFText
                    style={{ color: Colors.black }}
                    className={Styles.TopDay}
                  >
                    {dayMapping[cellToDisplay.day] || cellToDisplay.day}
                  </IFText>
                  <IFText
                    style={{ color: Colors.black }}
                    className={Styles.Session}
                  >
                    {`${cellToDisplay.value} ${t('Analytics.Session')}`}
                  </IFText>
                </div>
                <div style={{ color: Colors.black }} className={Styles.Session}>
                  {cellToDisplay.hour}
                </div>
              </>
            )}
          </div>
          <div className={Styles.Chart} ref={chartWrapperRef}>
            <ChartComponent
              type="heatmap"
              options={options}
              series={series}
              height="100%"
              width="100%"
              className={
                !(
                  fetchPeakHourRequestState === RequestState.LOADING ||
                  fetchPeakHourRequestState === RequestState.SUCCEEDED ||
                  fetchPeakHourRequestState == RequestState.UNINITIALIZED
                ) && Styles.OverlayTest
              }
            />

            {!(
              fetchPeakHourRequestState === RequestState.LOADING ||
              fetchPeakHourRequestState === RequestState.SUCCEEDED ||
              fetchPeakHourRequestState == RequestState.UNINITIALIZED
            ) && (
              <IFText
                className={Styles.OverlayText}
                style={{ color: Colors.red }}
              >
                {t('Analytics.NoData')}
              </IFText>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  peakHour: AnalyticsSelectors.getPeakHour(state),
  fetchPeakHourRequestState:
    AnalyticsSelectors.getFetchPeakHourRequestState(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchPeakHour: (filter) => dispatch(AnalyticsActions.fetchPeakHour(filter)),
})

PeakHourCard.propTypes = {
  filter: PropTypes.object,
  peakHour: PropTypes.object,
  fetchPeakHour: PropTypes.func,
  fetchPeakHourRequestState: PropTypes.number,
}

export default connect(mapStateToProps, mapDispatchToProps)(PeakHourCard)
